import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getHeaderTitle, HeaderBackContext, HeaderHeightContext, HeaderShownContext } from "@react-navigation/elements";
import { useTheme } from "@react-navigation/native";
import * as React from "react";
import { StyleSheet, View } from "react-native";
import ModalPresentationContext from "../../utils/ModalPresentationContext";
import useKeyboardManager from "../../utils/useKeyboardManager";
import Card from "./Card";
var EPSILON = 0.1;
function CardContainer(param) {
    var interpolationIndex = param.interpolationIndex, index = param.index, active = param.active, closing = param.closing, gesture = param.gesture, focused = param.focused, modal = param.modal, getPreviousScene = param.getPreviousScene, getFocusedRoute = param.getFocusedRoute, headerDarkContent = param.headerDarkContent, hasAbsoluteFloatHeader = param.hasAbsoluteFloatHeader, headerHeight = param.headerHeight, onHeaderHeightChange = param.onHeaderHeightChange, isParentHeaderShown = param.isParentHeaderShown, isNextScreenTransparent = param.isNextScreenTransparent, detachCurrentScreen = param.detachCurrentScreen, layout = param.layout, onCloseRoute = param.onCloseRoute, onOpenRoute = param.onOpenRoute, onGestureCancel = param.onGestureCancel, onGestureEnd = param.onGestureEnd, onGestureStart = param.onGestureStart, onTransitionEnd = param.onTransitionEnd, onTransitionStart = param.onTransitionStart, renderHeader = param.renderHeader, renderScene = param.renderScene, safeAreaInsetBottom = param.safeAreaInsetBottom, safeAreaInsetLeft = param.safeAreaInsetLeft, safeAreaInsetRight = param.safeAreaInsetRight, safeAreaInsetTop = param.safeAreaInsetTop, scene = param.scene;
    var parentHeaderHeight = React.useContext(HeaderHeightContext);
    var _useKeyboardManager = useKeyboardManager(React.useCallback(function() {
        var _scene_descriptor = scene.descriptor, options = _scene_descriptor.options, navigation = _scene_descriptor.navigation;
        return navigation.isFocused() && options.keyboardHandlingEnabled !== false;
    }, [
        scene.descriptor
    ])), onPageChangeStart = _useKeyboardManager.onPageChangeStart, onPageChangeCancel = _useKeyboardManager.onPageChangeCancel, onPageChangeConfirm = _useKeyboardManager.onPageChangeConfirm;
    var handleOpen = function() {
        var route = scene.descriptor.route;
        onTransitionEnd({
            route: route
        }, false);
        onOpenRoute({
            route: route
        });
    };
    var handleClose = function() {
        var route = scene.descriptor.route;
        onTransitionEnd({
            route: route
        }, true);
        onCloseRoute({
            route: route
        });
    };
    var handleGestureBegin = function() {
        var route = scene.descriptor.route;
        onPageChangeStart();
        onGestureStart({
            route: route
        });
    };
    var handleGestureCanceled = function() {
        var route = scene.descriptor.route;
        onPageChangeCancel();
        onGestureCancel({
            route: route
        });
    };
    var handleGestureEnd = function() {
        var route = scene.descriptor.route;
        onGestureEnd({
            route: route
        });
    };
    var handleTransition = function(param) {
        var closing = param.closing, gesture = param.gesture;
        var route = scene.descriptor.route;
        if (!gesture) {
            onPageChangeConfirm === null || onPageChangeConfirm === void 0 ? void 0 : onPageChangeConfirm(true);
        } else if (active && closing) {
            onPageChangeConfirm === null || onPageChangeConfirm === void 0 ? void 0 : onPageChangeConfirm(false);
        } else {
            onPageChangeCancel === null || onPageChangeCancel === void 0 ? void 0 : onPageChangeCancel();
        }
        onTransitionStart === null || onTransitionStart === void 0 ? void 0 : onTransitionStart({
            route: route
        }, closing);
    };
    var insets = {
        top: safeAreaInsetTop,
        right: safeAreaInsetRight,
        bottom: safeAreaInsetBottom,
        left: safeAreaInsetLeft
    };
    var colors = useTheme().colors;
    var _React_useState = _sliced_to_array(React.useState("box-none"), 2), pointerEvents = _React_useState[0], setPointerEvents = _React_useState[1];
    React.useEffect(function() {
        var _scene_progress_next_addListener, _scene_progress_next;
        var listener = (_scene_progress_next = scene.progress.next) === null || _scene_progress_next === void 0 ? void 0 : (_scene_progress_next_addListener = _scene_progress_next.addListener) === null || _scene_progress_next_addListener === void 0 ? void 0 : _scene_progress_next_addListener.call(_scene_progress_next, function(param) {
            var value = param.value;
            setPointerEvents(value <= EPSILON ? "box-none" : "none");
        });
        return function() {
            if (listener) {
                var _scene_progress_next_removeListener, _scene_progress_next;
                (_scene_progress_next = scene.progress.next) === null || _scene_progress_next === void 0 ? void 0 : (_scene_progress_next_removeListener = _scene_progress_next.removeListener) === null || _scene_progress_next_removeListener === void 0 ? void 0 : _scene_progress_next_removeListener.call(_scene_progress_next, listener);
            }
        };
    }, [
        pointerEvents,
        scene.progress.next
    ]);
    var _scene_descriptor_options = scene.descriptor.options, presentation = _scene_descriptor_options.presentation, animationEnabled = _scene_descriptor_options.animationEnabled, cardOverlay = _scene_descriptor_options.cardOverlay, cardOverlayEnabled = _scene_descriptor_options.cardOverlayEnabled, cardShadowEnabled = _scene_descriptor_options.cardShadowEnabled, cardStyle = _scene_descriptor_options.cardStyle, cardStyleInterpolator = _scene_descriptor_options.cardStyleInterpolator, gestureDirection = _scene_descriptor_options.gestureDirection, gestureEnabled = _scene_descriptor_options.gestureEnabled, gestureResponseDistance = _scene_descriptor_options.gestureResponseDistance, gestureVelocityImpact = _scene_descriptor_options.gestureVelocityImpact, headerMode = _scene_descriptor_options.headerMode, headerShown = _scene_descriptor_options.headerShown, transitionSpec = _scene_descriptor_options.transitionSpec;
    var previousScene = getPreviousScene({
        route: scene.descriptor.route
    });
    var backTitle;
    if (previousScene) {
        var _previousScene_descriptor = previousScene.descriptor, options = _previousScene_descriptor.options, route = _previousScene_descriptor.route;
        backTitle = getHeaderTitle(options, route.name);
    }
    var headerBack = React.useMemo(function() {
        return backTitle !== undefined ? {
            title: backTitle
        } : undefined;
    }, [
        backTitle
    ]);
    return /*#__PURE__*/ _jsx(Card, {
        interpolationIndex: interpolationIndex,
        gestureDirection: gestureDirection,
        layout: layout,
        insets: insets,
        gesture: gesture,
        current: scene.progress.current,
        next: scene.progress.next,
        closing: closing,
        onOpen: handleOpen,
        onClose: handleClose,
        overlay: cardOverlay,
        overlayEnabled: cardOverlayEnabled,
        shadowEnabled: cardShadowEnabled,
        onTransition: handleTransition,
        onGestureBegin: handleGestureBegin,
        onGestureCanceled: handleGestureCanceled,
        onGestureEnd: handleGestureEnd,
        gestureEnabled: index === 0 ? false : gestureEnabled,
        gestureResponseDistance: gestureResponseDistance,
        gestureVelocityImpact: gestureVelocityImpact,
        transitionSpec: transitionSpec,
        styleInterpolator: cardStyleInterpolator,
        accessibilityElementsHidden: !focused,
        importantForAccessibility: focused ? "auto" : "no-hide-descendants",
        pointerEvents: active ? "box-none" : pointerEvents,
        pageOverflowEnabled: headerMode !== "float" && presentation !== "modal",
        headerDarkContent: headerDarkContent,
        containerStyle: hasAbsoluteFloatHeader && headerMode !== "screen" ? {
            marginTop: headerHeight
        } : null,
        contentStyle: [
            {
                backgroundColor: presentation === "transparentModal" ? "transparent" : colors.background
            },
            cardStyle
        ],
        style: [
            {
                // This is necessary to avoid unfocused larger pages increasing scroll area
                // The issue can be seen on the web when a smaller screen is pushed over a larger one
                overflow: active ? undefined : "hidden",
                display: // Hide unfocused screens when animation isn't enabled
                // This is also necessary for a11y on web
                animationEnabled === false && isNextScreenTransparent === false && detachCurrentScreen !== false && !focused ? "none" : "flex"
            },
            StyleSheet.absoluteFill
        ],
        children: /*#__PURE__*/ _jsx(View, {
            style: styles.container,
            children: /*#__PURE__*/ _jsxs(ModalPresentationContext.Provider, {
                value: modal,
                children: [
                    /*#__PURE__*/ _jsx(View, {
                        style: styles.scene,
                        children: /*#__PURE__*/ _jsx(HeaderBackContext.Provider, {
                            value: headerBack,
                            children: /*#__PURE__*/ _jsx(HeaderShownContext.Provider, {
                                value: isParentHeaderShown || headerShown !== false,
                                children: /*#__PURE__*/ _jsx(HeaderHeightContext.Provider, {
                                    value: headerShown ? headerHeight : parentHeaderHeight !== null && parentHeaderHeight !== void 0 ? parentHeaderHeight : 0,
                                    children: renderScene({
                                        route: scene.descriptor.route
                                    })
                                })
                            })
                        })
                    }),
                    headerMode !== "float" ? renderHeader({
                        mode: "screen",
                        layout: layout,
                        scenes: [
                            previousScene,
                            scene
                        ],
                        getPreviousScene: getPreviousScene,
                        getFocusedRoute: getFocusedRoute,
                        onContentHeightChange: onHeaderHeightChange
                    }) : null
                ]
            })
        })
    });
}
export default /*#__PURE__*/ React.memo(CardContainer);
var styles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: "column-reverse"
    },
    scene: {
        flex: 1
    }
});
